export const MEDIA_KINDS = {
  image: {
    label: 'Image',
    accept: 'image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png',
    extensions: new Set(['jpg', 'jpeg', 'png']),
    deviceTypes: new Set(['image/jpeg', 'image/jpg', 'image/png']),
    matchesMime: (type = '') => type.startsWith('image/'),
  },
  video: {
    label: 'Video',
    accept: 'video/mp4,video/3gpp,.mp4,.3gp',
    extensions: new Set(['mp4', '3gp']),
    deviceTypes: new Set(['video/mp4', 'video/3gpp']),
    matchesMime: (type = '') => type.startsWith('video/'),
  },
  audio: {
    label: 'Audio',
    accept: 'audio/*,.mp3,.ogg,.aac,.m4a,.opus',
    extensions: new Set(['mp3', 'ogg', 'aac', 'm4a', 'opus', 'wav']),
    deviceTypes: new Set([
      'audio/ogg',
      'audio/mpeg',
      'audio/aac',
      'audio/mp4',
      'audio/x-m4a',
      'audio/wav',
      'audio/x-wav',
      'audio/webm',
    ]),
    matchesMime: (type = '') => type.startsWith('audio/'),
  },
  document: {
    label: 'Document',
    accept: 'application/pdf,.pdf,.xls,.xlsx,.doc,.docx,.csv,.txt',
    extensions: new Set(['pdf', 'xls', 'xlsx', 'doc', 'docx', 'csv', 'txt']),
    deviceTypes: new Set([
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/csv',
      'text/plain'
    ]),
    matchesMime: (type = '') => {
      const t = type.toLowerCase();
      return t && !t.startsWith('image/') && !t.startsWith('video/') && !t.startsWith('audio/');
    },
  },
};

export const filterMediaByKind = (items = [], kind) => {
  if (!kind || !MEDIA_KINDS[kind]) {
    return items;
  }
  return items.filter((item) => MEDIA_KINDS[kind].matchesMime(item?.type || ''));
};

export const isValidDeviceFileForKind = (file, kind) => {
  const config = MEDIA_KINDS[kind];
  if (!file || !config) {
    return false;
  }

  const mimeType = (file.type || '').toLowerCase();
  const extension = file.name?.split('.').pop()?.toLowerCase()?.split(/[?#]/)[0];

  if (mimeType && config.deviceTypes.has(mimeType)) {
    return true;
  }

  if (mimeType && config.matchesMime(mimeType)) {
    return true;
  }

  // macOS often reports PDFs as application/octet-stream.
  if (
    kind === 'document' &&
    extension === 'pdf' &&
    (!mimeType || mimeType === 'application/octet-stream')
  ) {
    return true;
  }

  return extension ? config.extensions.has(extension) : false;
};

/**
 * Maximum number of media files that can be selected/queued for a single send.
 */
export const MAX_SELECTED_MEDIA = 10;

/**
 * Resolves a WhatsApp media kind ('image' | 'video' | 'audio' | 'document')
 * from a file name or URL by inspecting its extension.
 * Uses MEDIA_KINDS as the single source of truth.
 *
 * @param {string|undefined} fileName
 * @returns {'image'|'video'|'audio'|'document'|null}
 */
export const getMediaTypeFromFileName = (fileName) => {
  const extension = fileName?.split('.').pop()?.split(/[?#]/)[0]?.toLowerCase();
  if (!extension) return null;
  for (const [kind, config] of Object.entries(MEDIA_KINDS)) {
    if (config.extensions.has(extension)) return kind;
  }
  return null;
};
